import { Flex, Text } from "@mantine/core";
import { IconFile } from "@tabler/icons-react";
import { type ReactNode, useCallback } from "react";
import { InterviewQuestionInputType, RequestStates } from "@/types";
import { SKIP_RESPONSE_VALUE, VOICE_S3_KEY_PREFIX } from "@/constants/voice";
import {
  translateIdentity,
  type StaticTextTranslator,
} from "@/utils/staticTextTranslator";
import { normalizeAssistantContent } from "@/utils/normalizeAssistantContent";
import { parseLinkedString } from "@/utils/parseLinkedString";
import { VoicePlaybackBubble } from "./VoicePlaybackBubble";
import styles from "./MessageContent.module.css";

interface MessageContentProps {
  /** Message index for unique keys */
  index: number;
  /** The message content (string or ReactNode) */
  content: string | ReactNode;
  /** Whether this is an incoming (assistant) message */
  incoming: boolean;
  /** Input type associated with the message */
  inputType?: InterviewQuestionInputType;
  /** Loading state */
  status?: RequestStates;
  /** Optional translation function for static transcript labels */
  translate?: StaticTextTranslator;
  /** Whether content is local static UI copy that should translate at render time */
  translateStaticContent?: boolean;
  /** Active session id — used to fetch presigned playback URLs for voice messages */
  sessionId?: string | null;
}

/**
 * Simple URL detection regex for https links
 */
const URL_REGEX = /https?:\/\/[^\s]+/g;

/**
 * Matches the full shape of a voice-transport S3 key
 * (`s3://<bucket>/voice-responses/<…>.webm`) rather than just the `s3://`
 * prefix, so unrelated S3 URLs cannot be misrendered as voice bubbles.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isVoiceTransportKey(content: string): boolean {
  return (
    content.startsWith(VOICE_S3_KEY_PREFIX) &&
    content.includes("/voice-responses/") &&
    content.endsWith(".webm")
  );
}

function normalizeForDisplay(content: string, incoming: boolean): string {
  return incoming ? normalizeAssistantContent(content) : content;
}

/**
 * Parses text content and converts URLs to clickable links
 */
function parseLinks(
  text: string,
  index: number,
  incoming: boolean,
): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(URL_REGEX);
  while ((match = regex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the link
    const url = match[0];
    parts.push(
      <a
        key={`message_${index}_link_${parts.length}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={incoming ? styles.incomingLink : styles.outgoingLink}
      >
        {url}
      </a>,
    );

    lastIndex = regex.lastIndex;
  }

  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Component for rendering message content with optional link parsing
 */
export const MessageContent = ({
  index,
  content,
  incoming,
  inputType,
  status = RequestStates.Success,
  translate = translateIdentity,
  translateStaticContent = false,
  sessionId = null,
}: MessageContentProps) => {
  const wrapContent = useCallback(
    (children: ReactNode) => (
      <Text
        className={incoming ? styles.incoming : styles.outgoing}
        data-loading={status === RequestStates.Loading}
      >
        {children}
      </Text>
    ),
    [incoming, status],
  );

  // If content is already JSX (ReactNode), render it directly
  if (typeof content !== "string") {
    return <>{content}</>;
  }

  if (
    !incoming &&
    inputType === InterviewQuestionInputType.FILE_UPLOAD &&
    content !== SKIP_RESPONSE_VALUE
  ) {
    const fileLabel = content.startsWith("s3://")
      ? translate("File Uploaded")
      : content;

    return (
      <Flex className={styles.fileBubble} gap="xs">
        <IconFile size={18} />
        <Text className={styles.fileName}>{fileLabel}</Text>
      </Flex>
    );
  }

  // AS-1816: outgoing voice answers transport an `s3://…` key as the
  // message payload. Render a standalone circular play-button (no text,
  // no surrounding rectangular bubble — `MessageWrapper` is in `bare`
  // mode for this row) that fetches the presigned GET URL on demand from
  // the interview-session API. Resumed transcripts can omit `inputType`,
  // so we accept either an explicit VOICE input type or no input type,
  // but always require the full voice-transport key shape so unrelated
  // S3 URLs are not rerouted.
  if (
    !incoming &&
    (inputType === InterviewQuestionInputType.VOICE || inputType == null) &&
    isVoiceTransportKey(content)
  ) {
    return (
      <VoicePlaybackBubble
        audioKey={content}
        sessionId={sessionId}
        translate={translate}
      />
    );
  }

  const normalizedContent = normalizeForDisplay(content, incoming);
  const displayContent =
    translateStaticContent || incoming || content === SKIP_RESPONSE_VALUE
      ? translate(normalizedContent)
      : normalizedContent;

  if (displayContent.includes("<a ")) {
    return wrapContent(
      <>
        {parseLinkedString(
          displayContent,
          incoming ? styles.incomingLink : styles.outgoingLink,
        ).map((node, nodeIndex) =>
          typeof node === "string" ? (
            node
          ) : (
            <span key={`message_${index}_html_${nodeIndex}`}>{node}</span>
          ),
        )}
      </>,
    );
  }

  // Parse URLs and wrap in Text component
  const parsedContent = parseLinks(displayContent, index, incoming);

  return wrapContent(parsedContent);
};
